<template>
  <Dialog
    :visible="modelValue"
    modal
    :header="isEdit ? 'Edit Transcript Type' : 'New Transcript Type'"
    class="w-full max-w-md"
    :draggable="false"
    @update:visible="emit('update:modelValue', $event)"
  >
    <form class="space-y-4 pt-2" data-testid="transcript-type-form" @submit.prevent="submit">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Name <span class="text-red-500">*</span></label>
        <InputText
          v-model="form.name"
          class="w-full"
          data-testid="transcript-type-name"
          :class="{ 'p-invalid': getErrorMessage('name') }"
        />
        <small v-if="getErrorMessage('name')" class="text-red-500">{{ getErrorMessage('name') }}</small>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Category <span class="text-red-500">*</span></label>
        <Select
          v-model="form.category"
          :options="categoryOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select a category"
          class="w-full"
          data-testid="transcript-type-category"
          :class="{ 'p-invalid': getErrorMessage('category') }"
        />
        <small v-if="getErrorMessage('category')" class="text-red-500">{{ getErrorMessage('category') }}</small>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <Textarea
          v-model="form.description"
          rows="3"
          class="w-full"
          autoResize
          data-testid="transcript-type-description"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Bill Item (fee)</label>
        <Select
          v-model="form.bill_item_template_id"
          :options="billItemOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="No fee (free)"
          showClear
          filter
          class="w-full"
          :loading="loadingBillItems"
          data-testid="transcript-type-bill-item"
        />
        <p class="text-xs text-gray-400 mt-1">If set, a bill is created when applicants submit a request of this type.</p>
      </div>

      <div class="flex items-center gap-2">
        <Checkbox
          v-model="form.is_active"
          binary
          inputId="transcript-type-active"
          data-testid="transcript-type-active"
        />
        <label for="transcript-type-active" class="text-sm text-gray-700">Active</label>
      </div>
    </form>

    <template #footer>
      <Button size="small"
        label="Cancel"
        severity="secondary"
        text
        data-testid="transcript-type-cancel"
        @click="close"
      />
      <Button size="small"
        :label="isEdit ? 'Save' : 'Create'"
        :loading="creatingType || updatingType"
        data-testid="transcript-type-submit"
        @click="submit"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useFormErrors } from '@/composables/useFormErrors';
import { useTranscriptMutations } from '@/composables/transcript/useTranscriptMutations';
import { useGetBillItemTemplatesQuery } from '@/types/vue-apollo-helpers';
import {
  TranscriptTypeCategoryEnum,
  type TranscriptType,
} from '@/types/composition-functions';

const props = defineProps<{
  modelValue: boolean;
  type?: TranscriptType | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  saved: [];
}>();

const isEdit = computed(() => !!props.type?.id);

const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();
const {
  createTranscriptType,
  updateTranscriptType,
  creatingType,
  updatingType,
} = useTranscriptMutations();

const initialForm = () => ({
  name: '',
  category: TranscriptTypeCategoryEnum.StudentCopy as TranscriptTypeCategoryEnum,
  description: '',
  bill_item_template_id: undefined as string | undefined,
  is_active: true,
});

const form = reactive(initialForm());

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      clearErrors();
      if (props.type) {
        form.name = props.type.name;
        form.category = props.type.category;
        form.description = props.type.description ?? '';
        form.bill_item_template_id = props.type.billItemTemplate?.id ?? undefined;
        form.is_active = props.type.is_active;
      } else {
        Object.assign(form, initialForm());
      }
    }
  },
);

const categoryOptions = [
  { label: 'Student Copy', value: TranscriptTypeCategoryEnum.StudentCopy },
  { label: 'Official Soft Copy', value: TranscriptTypeCategoryEnum.OfficialSoftCopy },
  { label: 'Official Hard Copy', value: TranscriptTypeCategoryEnum.OfficialHardCopy },
];

const { result: billItemsResult, loading: loadingBillItems } = useGetBillItemTemplatesQuery({
  is_active: true,
});

const billItemOptions = computed(() =>
  (billItemsResult.value?.billItemTemplates ?? []).map((b) => ({
    label: `${b.name} — ₦${Number(b.default_amount ?? 0).toLocaleString()}`,
    value: b.id,
  })),
);

const close = () => emit('update:modelValue', false);

const submit = async() => {
  clearErrors();
  try {
    if (isEdit.value && props.type) {
      await updateTranscriptType({
        id: props.type.id,
        name: form.name,
        category: form.category,
        description: form.description || undefined,
        bill_item_template_id: form.bill_item_template_id || undefined,
        is_active: form.is_active,
      });
    } else {
      await createTranscriptType({
        name: form.name,
        category: form.category,
        description: form.description || undefined,
        bill_item_template_id: form.bill_item_template_id || undefined,
        is_active: form.is_active,
      });
    }
    emit('saved');
    close();
  } catch (err) {
    handleServerErrors(err);
  }
};
</script>
